import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '../utils/toast';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  GripVertical,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  HelpCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingSettingsPage = () => {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState(null);

  useEffect(() => {
    fetchSurvey();
  }, []);

  const fetchSurvey = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/onboarding-survey`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSurvey(response.data.survey);
        // Expand all sections by default
        const expanded = {};
        response.data.survey.sections?.forEach(section => {
          expanded[section.section_id] = true;
        });
        setExpandedSections(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch survey:', error);
      toast.error('Failed to load survey configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveSurvey = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/admin/onboarding-survey`,
        survey,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Survey configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save survey:', error);
      toast.error('Failed to save survey configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const addSection = () => {
    const newSubchapter = {
      section_id: `section_${Date.now()}`,
      title: 'New Section',
      questions: []
    };
    setSurvey(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setExpandedSections(prev => ({
      ...prev,
      [newSection.section_id]: true
    }));
  };

  const updateSectionTitle = (sectionId, title) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.section_id === sectionId
          ? { ...section, title }
          : section
      )
    }));
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section and all its questions?')) return;
    
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.section_id !== sectionId)
    }));
  };

  const startAddQuestion = (sectionId) => {
    setNewQuestion({
      section_id: sectionId,
      question_id: `q_${Date.now()}`,
      question_text: '',
      question_type: 'single_select',
      is_required: false,
      options: [{ option_id: 'opt1', text: '' }]
    });
  };

  const addOptionToQuestion = (questionData, setQuestionData) => {
    const newOption = {
      option_id: `opt_${Date.now()}`,
      text: ''
    };
    setQuestionData({
      ...questionData,
      options: [...questionData.options, newOption]
    });
  };

  const updateOption = (questionData, setQuestionData, optionId, text) => {
    setQuestionData({
      ...questionData,
      options: questionData.options.map(opt =>
        opt.option_id === optionId ? { ...opt, text } : opt
      )
    });
  };

  const removeOption = (questionData, setQuestionData, optionId) => {
    if (questionData.options.length <= 1) {
      toast.error('Question must have at least one option');
      return;
    }
    setQuestionData({
      ...questionData,
      options: questionData.options.filter(opt => opt.option_id !== optionId)
    });
  };

  const saveNewQuestion = () => {
    if (!newQuestion.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (newQuestion.options.some(opt => !opt.text.trim())) {
      toast.error('All options must have text');
      return;
    }

    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.section_id === newQuestion.section_id
          ? { ...section, questions: [...section.questions, newQuestion] }
          : section
      )
    }));
    setNewQuestion(null);
  };

  const startEditQuestion = (sectionId, question) => {
    setEditingQuestion({
      section_id: sectionId,
      ...question
    });
  };

  const saveEditQuestion = () => {
    if (!editingQuestion.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (editingQuestion.options.some(opt => !opt.text.trim())) {
      toast.error('All options must have text');
      return;
    }

    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.section_id === editingQuestion.section_id
          ? {
              ...section,
              questions: section.questions.map(q =>
                q.question_id === editingQuestion.question_id
                  ? editingQuestion
                  : q
              )
            }
          : section
      )
    }));
    setEditingQuestion(null);
  };

  const deleteQuestion = (sectionId, questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.section_id === sectionId
          ? {
              ...section,
              questions: section.questions.filter(q => q.question_id !== questionId)
            }
          : section
      )
    }));
  };

  const QuestionForm = ({ questionData, setQuestionData, onSave, onCancel }) => (
    <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
      <div>
        <Label>Question Text</Label>
        <Textarea
          value={questionData.question_text}
          onChange={(e) => setQuestionData({ ...questionData, question_text: e.target.value })}
          placeholder="Enter your question..."
          className="mt-1"
        />
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label>Question Type:</Label>
          <select
            value={questionData.question_type}
            onChange={(e) => setQuestionData({ ...questionData, question_type: e.target.value })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="single_select">Single Select</option>
            <option value="multi_select">Multi Select</option>
          </select>
        </div>
        
        {questionData.question_type === 'multi_select' && (
          <div className="flex items-center gap-2">
            <Label>Max Selections:</Label>
            <Input
              type="number"
              value={questionData.max_selections || ''}
              onChange={(e) => setQuestionData({ ...questionData, max_selections: parseInt(e.target.value) || null })}
              className="w-20"
              min="1"
            />
          </div>
        )}
        
        <div className="flex items-center gap-2">
          <Switch
            checked={questionData.is_required}
            onCheckedChange={(checked) => setQuestionData({ ...questionData, is_required: checked })}
          />
          <Label>Required</Label>
        </div>
      </div>
      
      <div>
        <Label className="mb-2 block">Options</Label>
        <div className="space-y-2">
          {questionData.options.map((option, index) => (
            <div key={option.option_id} className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
              <Input
                value={option.text}
                onChange={(e) => updateOption(questionData, setQuestionData, option.option_id, e.target.value)}
                placeholder="Option text..."
                className="flex-1"
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={option.has_text_input || false}
                  onCheckedChange={(checked) => {
                    setQuestionData({
                      ...questionData,
                      options: questionData.options.map(opt =>
                        opt.option_id === option.option_id
                          ? { ...opt, has_text_input: checked }
                          : opt
                      )
                    });
                  }}
                />
                <Label className="text-xs whitespace-nowrap">Text Input</Label>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeOption(questionData, setQuestionData, option.option_id)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => addOptionToQuestion(questionData, setQuestionData)}
          className="mt-2"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Option
        </Button>
      </div>
      
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <Button onClick={onSave}>
          <Save className="w-4 h-4 mr-1" /> Save Question
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-blue-600" />
              Institution Onboarding Settings
            </h1>
            <p className="text-gray-600 mt-1">
              Manage questionnaires and surveys shown during institution admin onboarding
            </p>
          </div>
          <Button onClick={saveSurvey} disabled={saving}>
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Training Cost Survey Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              Training Cost Survey
            </CardTitle>
            <CardDescription>
              This survey is shown to institution admins during onboarding when they select the "Cost-Effectiveness" dashboard module.
              Manage questions, options, and sections below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Label>Survey Title</Label>
              <Input
                value={survey?.title || ''}
                onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
                className="mt-1 max-w-md"
              />
            </div>
            <div className="mb-6">
              <Label>Survey Description</Label>
              <Textarea
                value={survey?.description || ''}
                onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {survey?.sections?.map((section, sectionIndex) => (
                <div key={section.section_id} className="border rounded-lg overflow-hidden">
                  {/* Subchapter Header */}
                  <div 
                    className="bg-gray-100 p-3 flex items-center justify-between cursor-pointer hover:bg-gray-200"
                    onClick={() => toggleSection(section.section_id)}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-700">
                        Subchapter {sectionIndex + 1}:
                      </span>
                      <Input
                        value={section.title}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateSectionTitle(section.section_id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-64 h-8"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {section.questions.length} question(s)
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSection(section.section_id);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedSections[section.section_id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Subchapter Content */}
                  {expandedSections[section.section_id] && (
                    <div className="p-4 space-y-4">
                      {/* Questions */}
                      {section.questions.map((question, qIndex) => (
                        <div key={question.question_id} className="border rounded-lg p-4 bg-white">
                          {editingQuestion?.question_id === question.question_id ? (
                            <QuestionForm
                              questionData={editingQuestion}
                              setQuestionData={setEditingQuestion}
                              onSave={saveEditQuestion}
                              onCancel={() => setEditingQuestion(null)}
                            />
                          ) : (
                            <div>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-medium text-blue-600">
                                      Q{qIndex + 1}.
                                    </span>
                                    {question.is_required && (
                                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                                        Required
                                      </span>
                                    )}
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                      {question.question_type === 'multi_select' 
                                        ? `Multi-select${question.max_selections ? ` (max ${question.max_selections})` : ''}`
                                        : 'Single select'}
                                    </span>
                                  </div>
                                  <p className="text-gray-800 mb-3">{question.question_text}</p>
                                  <div className="space-y-1">
                                    {question.options.map((option, oIndex) => (
                                      <div key={option.option_id} className="flex items-center gap-2 text-sm text-gray-600">
                                        <span className="w-4 h-4 border rounded-full flex items-center justify-center text-xs">
                                          {String.fromCharCode(65 + oIndex)}
                                        </span>
                                        {option.text}
                                        {option.has_text_input && (
                                          <span className="text-xs text-blue-500">(with text input)</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEditQuestion(section.section_id, question)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteQuestion(section.section_id, question.question_id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add New Question Form */}
                      {newQuestion?.section_id === section.section_id ? (
                        <QuestionForm
                          questionData={newQuestion}
                          setQuestionData={setNewQuestion}
                          onSave={saveNewQuestion}
                          onCancel={() => setNewQuestion(null)}
                        />
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => startAddQuestion(section.section_id)}
                          className="w-full"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Question
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Subchapter Button */}
            <Button
              variant="outline"
              onClick={addSection}
              className="mt-4 w-full"
            >
              <Plus className="w-4 h-4 mr-1" /> Add New Section
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default OnboardingSettingsPage;
